import type {
  LocationRequest,
  LocationResponse,
  LocationTargetResponse,
  PageResponseLocationResponse,
  PageResponseLocationWithSpacesResponse,
  PersonOfContactResponse,
  PostgresFilePointer,
  ReportResponse,
} from '@/api/data-contracts';
import useLocationsService from '@/features/locations/api/locations.service';
import { queryKeys } from '@/react-query/constants';
import type { FeatureQueryOptions } from '@/shared/api';

import { locationsApi } from './locationsApi';
import useReports from './reports.service';

const { keys } = locationsApi;

type ExtraQueryOptions<TData> = Omit<FeatureQueryOptions<TData>, 'queryKey' | 'queryFn'>;

/**
 * Fetches a paginated list of locations.
 * Key: keys.list(request) — rooted on queryKeys.allLocations for prefix-invalidation safety.
 * All consumers use [queryKeys.allLocations] as a flat key; keys.list(request) is a strict
 * superset so existing invalidations via [queryKeys.allLocations] continue to work.
 */
export const useLocationsListQuery = (
  request: LocationRequest,
  enabled = true,
  options?: ExtraQueryOptions<PageResponseLocationResponse>,
) => {
  const { getLocations } = useLocationsService();
  return locationsApi.useFeatureQuery({
    queryKey: keys.list(request),
    queryFn: (signal) => getLocations({ request }, signal),
    enabled,
    ...options,
  });
};

export const usePersonOfContactCandidatesListQuery = (
  companyId: string,
  enabled = true,
  options?: ExtraQueryOptions<PersonOfContactResponse[]>,
) => {
  const { getContactCandidates } = useLocationsService();
  return locationsApi.useFeatureQuery({
    queryKey: [queryKeys.locationContactCandidates, companyId],
    queryFn: (signal) => getContactCandidates(companyId, signal),
    enabled: enabled && !!companyId,
    ...options,
  });
};

/**
 * Fetches locations together with their spaces.
 * Key: [queryKeys.companyLocationsWithSpaces, request] — rooted on the dedicated
 * companyLocationsWithSpaces key to serve CompanyLocationsTable's caching pattern.
 *
 * Migration-phase note: consumers that currently call getLocationsWithSpaces under a
 * different key (e.g. OrganizationLocations keyed on [queryKeys.allLocations]) represent
 * a divergence from this hook. They either keep their own read or accept a cache-key
 * change when they flip to this hook.
 */
export const useLocationsWithSpacesQuery = (
  request: LocationRequest,
  enabled = true,
  options?: ExtraQueryOptions<PageResponseLocationWithSpacesResponse>,
) => {
  const { getLocationsWithSpaces } = useLocationsService();
  return locationsApi.useFeatureQuery({
    queryKey: [queryKeys.companyLocationsWithSpaces, request],
    queryFn: (signal) => getLocationsWithSpaces({ request }, signal),
    enabled,
    ...options,
  });
};

/**
 * Fetches a single location by id.
 * Key: [queryKeys.location, locationId].
 * Call-site evidence: LocationFormPage.tsx L49, LocationReportsPage.tsx L99,
 * AdvertisingSpaceFormPreview.tsx L39.
 */
export const useLocationDetailsQuery = (
  locationId: string,
  enabled = true,
  options?: ExtraQueryOptions<LocationResponse>,
) => {
  const { getLocationDetails } = useLocationsService();
  return locationsApi.useFeatureQuery({
    queryKey: [queryKeys.location, locationId],
    queryFn: (signal) => getLocationDetails(locationId, signal),
    enabled: enabled && !!locationId,
    ...options,
  });
};

/**
 * Fetches the city ↔ district map for all locations.
 * Key: [queryKeys.locationCities].
 * Call-site evidence: OnboardingStep4.tsx L39, OrderBookingsTableAddSpaceBooking.tsx L223,
 * useAdvertisingSpacesData.ts L34, useAdvertisingPackageListData.ts L50.
 */
export const useLocationCitiesQuery = (
  enabled = true,
  options?: ExtraQueryOptions<Record<string, string[]>>,
) => {
  const { getLocationCities } = useLocationsService();
  return locationsApi.useFeatureQuery({
    queryKey: [queryKeys.locationCities],
    queryFn: (signal) => getLocationCities(signal),
    enabled,
    ...options,
  });
};

/**
 * Fetches the floor-map file pointers for a location.
 * Key: [queryKeys.locationFloorMaps, locationId].
 * Call-site evidence: LocationFloorMapsDialog.tsx L37, LocationPlanMap.tsx L71,
 * CompanyLocationsTable.tsx L251, FloorPlanDialog.tsx L75, SpacePositionOnMap/index.tsx L45.
 */
export const useLocationFloorMapsQuery = (
  locationId: string,
  enabled = true,
  options?: ExtraQueryOptions<Record<string, PostgresFilePointer[]>>,
) => {
  const { getLocationFloorMaps } = useLocationsService();
  return locationsApi.useFeatureQuery({
    queryKey: [queryKeys.locationFloorMaps, locationId],
    queryFn: (signal) => getLocationFloorMaps(locationId, signal),
    enabled: enabled && !!locationId,
    ...options,
  });
};

/**
 * Fetches revenue targets for the given location IDs (or all locations when undefined).
 * Key: [queryKeys.locationTargets, locationIds] when ids are provided;
 *      [queryKeys.locationTargets] (flat) when called with no ids.
 *
 * The flat key matches what useLocationStatistics and useServicesStatistics expect, so
 * no-arg calls share the same cache entry as those consumers after migration. Calls with
 * ids (e.g. LocationReportsPage passing [params?.id]) key on the longer form for
 * cache isolation. The flat [queryKeys.locationTargets] invalidation in TargetForm.tsx
 * still clears all variants via React Query prefix-matching.
 */
/**
 * Fetches the contact candidates for a company's person-of-contact picker.
 * Key: [queryKeys.locationContactCandidates, companyId].
 * Call-site evidence: LocationPersonContact.tsx.
 */
export const useContactCandidatesQuery = (
  companyId: string,
  enabled = true,
  options?: ExtraQueryOptions<PersonOfContactResponse[]>,
) => {
  const { getContactCandidates } = useLocationsService();
  return locationsApi.useFeatureQuery({
    queryKey: [queryKeys.locationContactCandidates, companyId],
    queryFn: (signal) => getContactCandidates(companyId, signal),
    enabled: enabled && !!companyId,
    ...options,
  });
};

export const useLocationTargetsQuery = (
  locationIds?: string[],
  enabled = true,
  options?: ExtraQueryOptions<LocationTargetResponse>,
) => {
  const { getLocationTargets } = useLocationsService();
  return locationsApi.useFeatureQuery({
    queryKey:
      locationIds !== undefined
        ? [queryKeys.locationTargets, locationIds]
        : [queryKeys.locationTargets],
    queryFn: (signal) => getLocationTargets(locationIds, signal),
    enabled,
    ...options,
  });
};

export const useLocationReportQuery = (
  locationId: string | undefined,
  topXClients: number,
  options?: ExtraQueryOptions<ReportResponse>,
) => {
  const { getLocationReport } = useReports();
  return locationsApi.useFeatureQuery({
    queryKey: keys.sub('report', locationId, topXClients),
    queryFn: (signal) => getLocationReport(locationId!, { topXClients }, signal),
    enabled: Boolean(locationId),
    ...options,
  });
};
